import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { AssetStyleId, WaveformShape } from 'fym-shared/src/music24/assetStyles';
import { motionArtworkCoverNote } from 'fym-shared/src/music24/story/motionArtwork';
import { assetUsesInstructions } from 'fym-shared/src/music24/assetInstructions';
import { TypedMemo } from '../../../../common/types';
import { Panel } from '../../../components/primitives/Panel';
import { studioCredits } from '../studioRules';
import { isMomentReady } from '../fullVideoRules';
import type { DetailsStepProps } from './detailsStepProps';
import { StudioRail } from './StudioRail';
import { StylePicker } from './StylePicker';
import { InstructionsField } from './InstructionsField';
import { DetailsFields } from './DetailsFields';
import { RailNotice } from './RailNotice';
import { lyricsRowState } from './FullVideoLyricsRow';
import { useStudioLyricLines } from './useStudioLyricLines';
import { useRailExtras } from './useRailExtras';
import { useImageWidth } from './useImageSize';
import { usePreviewLyric } from './usePreviewLyric';
import { useThemeStillSong } from '../../releases/theme/useThemeStillSong';
import { AssetFrame, isFrameAssetType } from '../frames/AssetFrame';
import { isShortPreviewType, ShortVideoPreview } from '../frames/ShortVideoPreview';
import { useStoryPreviewContext } from './imageTiles/useStoryPreviewContext';
import { useIsNarrowScreen } from '../../work/useIsNarrowScreen';
import { studioMessages, studioMomentMessages } from '../studioMessages';
import { fullVideoMessages } from '../fullVideoMessages';
import { catalogArtworkOriginalSrc } from '../../../../smartLinks/catalog/catalogArtwork';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Studio; each part uses its own slice of it */
import styles from '../Studio.module.scss';
/* eslint-enable css-modules/no-unused-class */

/**
 * Step 3 (designs n1–n3b, q3b, q3c, s7, s7b): the fields of the asset type, the style (only the
 * styles its asset type offers, the theme default first selected), the Instructions (a video and
 * a loop; an image has none, owner decision 2026-10-08; a lyric video that code lays out has none,
 * design y3) and the rail with the preview and "Make it". The full videos and the lyric moment
 * show their fields before the style (s7, s7b); the music video has one style, so no Style section.
 * The AI reel has its own step 3 (`AiReelDetails`).
 */
const MediaDetailsStepComponent: React.FC<DetailsStepProps> = (props) => {
  const { accountId, item, themeId, details, song, onChange, items, state, isOwner, isMaking, isLookSaving } = props;
  const { songTheme, style, instructions, defaultInstructions, frameLyric, onOpenSongLyrics, onMake } = props;
  const { actionFeedback } = props;
  const intl = useIntl();
  const stillSong = useThemeStillSong(item);
  const kicker =
    details.moment === undefined ? stillSong.kicker : intl.formatMessage(studioMomentMessages[details.moment]);
  const changeStyle = useCallback(
    (next: AssetStyleId, waveform?: WaveformShape) => onChange({ style: next, waveform }),
    [onChange]
  );
  const changeInstructions = useCallback((text: string) => onChange({ instructions: text }), [onChange]);
  const resetInstructions = useCallback(() => onChange({ instructions: undefined }), [onChange]);
  const credits = useMemo(() => studioCredits(themeId, items), [items, themeId]);

  const isSyncing = lyricsRowState(song) === 'syncing';
  const lyricLines = useStudioLyricLines({
    accountId,
    itemUuid: item.uuid,
    lyrics: song.lyrics,
    isSyncing,
    skip: themeId !== 'lyricMoment',
  });
  const extras = useRailExtras({ themeId, details, durationSec: song.durationSec, lines: lyricLines.lines });
  const lyric = usePreviewLyric(themeId, details, lyricLines.lines, frameLyric);
  const momentReady =
    themeId !== 'lyricMoment' ||
    isMomentReady({ isSyncing, timing: lyricLines.timing, lineFrom: details.lineFrom, lineTo: details.lineTo });

  const songFacts = useMemo(() => ({ ...stillSong, kicker }), [kicker, stillSong]);
  // The reason "Make it" is off for a field of step 3. The plan and the clip bank have their own
  // notice (`RailNotice`).
  let blockedReason: string | null = null;
  if (state === 'open' && isLookSaving) {
    blockedReason = intl.formatMessage(studioMessages.blockedSavingTheme);
  } else if (state === 'open' && themeId === 'lyricMoment' && !momentReady) {
    blockedReason =
      details.lineFrom === undefined || details.lineTo === undefined
        ? intl.formatMessage(studioMessages.blockedPickLines)
        : intl.formatMessage(fullVideoMessages.linesNotTimed);
  } else if (state === 'open' && items === null) {
    blockedReason = intl.formatMessage(studioMessages.blockedFields);
  }
  const narrow = useIsNarrowScreen();
  // Design w: a cover under 3840 px is scaled up and soft; say so, never fail.
  // The width check reads the original cover; the stills draw a sized copy (imgproxy).
  const coverWidth = useImageWidth(themeId === 'motionArtwork' ? (catalogArtworkOriginalSrc(item) ?? null) : null);
  const coverNote = themeId === 'motionArtwork' ? motionArtworkCoverNote(coverWidth) : null;
  const shortContext = useStoryPreviewContext(item, song, songTheme, stillSong.artworkUrl);
  const shortPreview = isShortPreviewType(themeId) ? (
    <ShortVideoPreview
      assetTypeId={themeId}
      context={shortContext}
      style={style?.style ?? null}
      shape={details.shape}
      lyric={lyric}
      width={narrow ? 220 : 300}
    />
  ) : undefined;
  const framePreview =
    isFrameAssetType(themeId) && style !== null ? (
      <AssetFrame
        assetTypeId={themeId}
        assetStyle={style.style}
        waveform={style.waveform}
        look={songTheme.look}
        palette={songTheme.palette}
        song={songFacts}
        lyric={lyric}
      />
    ) : undefined;
  const preview = shortPreview ?? framePreview;
  // Only an asset whose director reads the text shows the field (`assetUsesInstructions`).
  const showsInstructions = assetUsesInstructions({
    assetTypeId: themeId,
    releaseThemeId: songTheme.look.themeId,
    style: style?.style,
  });
  const styleAfterFields = isFrameAssetType(themeId) && themeId !== 'motionArtwork';
  // The music video has one style; the motion artwork has none (the theme gives the motion, design w).
  const stylePicker = style !== null && themeId !== 'musicVideo' && themeId !== 'motionArtwork' && (
    <StylePicker
      assetTypeId={themeId}
      look={songTheme.look}
      palette={songTheme.palette}
      song={songFacts}
      value={style}
      canUseAiReels={song.canUseAiReels}
      lyric={lyric}
      onChange={changeStyle}
    />
  );

  return (
    <div className={styles.details}>
      <div className={styles.detailsMain}>
        {preview !== undefined && <div className={styles.narrowPreview}>{preview}</div>}
        <Panel>
          {!styleAfterFields && stylePicker}
          <DetailsFields
            accountId={accountId}
            itemUuid={item.uuid}
            themeId={themeId}
            details={details}
            song={song}
            onChange={onChange}
            lyricLines={lyricLines}
            onOpenSongLyrics={onOpenSongLyrics}
          />
          {coverNote !== null && (
            <p className="small text-warning" data-testid="motion-cover-note">
              {intl.formatMessage(fullVideoMessages.motionCoverSmall, { size: coverNote.coverPx })}
            </p>
          )}
          {styleAfterFields && stylePicker}
          {showsInstructions && (
            <InstructionsField
              value={instructions}
              defaultValue={defaultInstructions}
              onChange={changeInstructions}
              onReset={resetInstructions}
            />
          )}
        </Panel>
      </div>
      <StudioRail
        accountId={accountId}
        themeId={themeId}
        details={details}
        releaseThemeId={songTheme.look.themeId}
        style={style}
        credits={credits}
        canMake={items !== null && state === 'open' && momentReady && !isMaking && !isLookSaving}
        isMaking={isMaking}
        onMake={onMake}
        preview={preview}
        extraRows={extras.rows}
        wait={extras.wait}
        notice={<RailNotice themeId={themeId} state={state} isOwner={isOwner} />}
        blockedReason={blockedReason}
        actionFeedback={actionFeedback}
      />
    </div>
  );
};

/** Step 3 of a video and a loop. An image has its own step (`ImageDetailsStep`). */
export const DetailsStep = TypedMemo(MediaDetailsStepComponent);
